// =====================================================
// Home Card - Display Home Details with Photo Carousel
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { HomeCard as HomeCardType } from '../../types';
import TourRequestForm from '../TourRequestForm';
import { useChatStore } from '../../store/chatStore';

interface HomeCardProps {
  card: HomeCardType;
  onFormToggle?: (isOpen: boolean) => void;
}

const AUTOPLAY_INTERVAL = 4000; // 4 seconds between slides

const HomeCard: React.FC<HomeCardProps> = ({ card, onFormToggle }) => {
  const { isHomeSubmitted, openPhotoModal, openVirtualTourModal } = useChatStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [validImages, setValidImages] = useState<string[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [showTourForm, setShowTourForm] = useState(false);
  const isSubmitted = isHomeSubmitted(card.home_id);

  // Notify parent when form opens/closes
  const handleFormToggle = (isOpen: boolean) => {
    setShowTourForm(isOpen);
    onFormToggle?.(isOpen);
  };

  // Filter out broken/invalid images on mount
  useEffect(() => {
    const checkImages = async () => {
      const validUrls: string[] = [];

      for (const url of card.images) {
        // Skip empty, null, undefined, or obviously invalid URLs
        if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
          continue;
        }

        // Skip URLs that don't look valid (should start with http, https, or /)
        const trimmedUrl = url.trim();
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://') && !trimmedUrl.startsWith('/')) {
          continue;
        }

        try {
          const isValid = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = trimmedUrl;
            // Timeout after 5 seconds
            setTimeout(() => resolve(false), 5000);
          });

          if (isValid) {
            validUrls.push(trimmedUrl);
          }
        } catch {
          // Skip broken images
        }
      }

      // If no valid images, use placeholder
      if (validUrls.length === 0) {
        validUrls.push('https://via.placeholder.com/400x300?text=Home+Image');
      }

      setValidImages(validUrls);
      setImagesLoaded(true);
    };

    checkImages();
  }, [card.images]);

  // Autoplay functionality
  useEffect(() => {
    if (!imagesLoaded || validImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev < validImages.length - 1 ? prev + 1 : 0
      );
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [imagesLoaded, validImages.length]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Format completion date as "JAN 22, 2026" (using UTC to avoid timezone shift)
  const formatCompletionDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${month} ${day}, ${year}`;
  };

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : validImages.length - 1
    );
  }, [validImages.length]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev < validImages.length - 1 ? prev + 1 : 0
    );
  }, [validImages.length]);

  const handleCTAClick = (action: string, url?: string) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      // Dispatch custom event for chatbot actions
      const event = new CustomEvent('chatbot-action', {
        detail: { action }
      });
      window.dispatchEvent(event);
    }
  };

  // Handle image click to open photo enlargement modal
  const handleImageClick = () => {
    if (validImages.length > 0) {
      openPhotoModal(validImages, currentImageIndex, (finalIndex) => {
        // Sync carousel to the image user was viewing when modal closed
        setCurrentImageIndex(finalIndex);
      }, card.street_address);
    }
  };

  // Show placeholder while checking images
  const displayImages = imagesLoaded ? validImages : card.images;

  // Show tour request form instead of card when requested
  if (showTourForm) {
    return (
      <TourRequestForm
        isOpen={showTourForm}
        onClose={() => handleFormToggle(false)}
        prefillData={{
          cityOfInterest: card.region_name || card.city,
          communityOfInterest: card.community_name,
          addressOrFloorPlan: card.street_address,
          // Separate columns for home address and floor plan name
          homeAddress: card.street_address,
          floorPlanName: card.plan_name,
          // Track as Quick Move-In inquiry
          inquiryType: 'quick_move_in',
          homeId: card.home_id,
          // QMI homes are always linked to a floor plan
          floorPlanId: card.floor_plan_id,
          communityId: card.community_id,
          regionId: card.region_id
        }}
      />
    );
  }

  return (
    <div className="chat-card animate-slide-up">
      {/* Photo Carousel */}
      <div className="relative h-48 bg-gray-200">
        <img
          src={displayImages[currentImageIndex] || 'https://via.placeholder.com/400x300?text=Home+Image'}
          alt={`${card.street_address} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300 cursor-pointer"
          onClick={handleImageClick}
          onError={(e) => {
            e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Home+Image';
          }}
        />

        {/* Price Badge */}
        <div className="absolute top-2 right-2 bg-betenbough-olive text-white
                        px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
          {formatPrice(card.price)}
        </div>

        {/* Status Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {card.move_in_ready ? (
            <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold shadow-lg">
              MOVE-IN READY
            </span>
          ) : card.estimated_completion_date && (
            <span className="bg-teal-600 text-white px-2 py-1 rounded text-xs font-semibold shadow-lg">
              EST COMPLETION {formatCompletionDate(card.estimated_completion_date)}
            </span>
          )}
          {card.status && (
            <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold shadow-lg">
              {card.status}
            </span>
          )}
          {card.incentives_text && (
            <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold shadow-lg">
              $ Incentives
            </span>
          )}
        </div>

        {/* Carousel Navigation (only if multiple valid images) */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70
                         text-white rounded-full p-1.5 transition-all"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70
                         text-white rounded-full p-1.5 transition-all"
              aria-label="Next image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image Dots */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {displayImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    index === currentImageIndex
                      ? 'bg-white w-4'
                      : 'bg-white/50'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Virtual Tour Button - bottom right of carousel */}
        {card.virtual_tour_url && (
          <button
            onClick={() => openVirtualTourModal(card.virtual_tour_url!, card.street_address)}
            className="absolute bottom-2 right-2 bg-black/60 hover:bg-black/80 text-white
                       rounded-full p-1.5 transition-all shadow-lg"
            title="Virtual Tour"
            aria-label="View virtual tour"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        )}
      </div>

      {/* Expand Hint */}
      <p className="text-[10px] text-gray-400 text-center py-1">Click image to expand</p>

      {/* Details */}
      <div className="p-4">
        <h3 className="font-bold text-lg text-gray-900 mb-1">
          {card.street_address}
        </h3>
        <div className="flex items-center gap-1.5 mb-3">
          <p className="text-sm text-gray-600">
            {card.city}, {card.state} {card.zip_code}
          </p>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${card.street_address}, ${card.city}, ${card.state} ${card.zip_code}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-betenbough-olive transition-colors"
            title="View on map"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              {/* Folded map with road */}
              <path d="M2 19l5-2 5 2 5-2 5 2V5l-5 2-5-2-5 2-5-2v14z" opacity="0.5"/>
              <path d="M7 7v10M12 5v12M17 7v10" stroke="currentColor" strokeWidth="1" opacity="0.3" fill="none"/>
              {/* Location pin */}
              <path d="M17 3c-2.2 0-4 1.8-4 4 0 3 4 6 4 6s4-3 4-6c0-2.2-1.8-4-4-4zm0 5.5c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z"/>
            </svg>
          </a>
        </div>

        {card.floor_plan_url ? (
          <a
            href={card.floor_plan_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-betenbough-olive font-semibold mb-2 hover:underline cursor-pointer block"
          >
            {card.plan_name} Floor Plan
          </a>
        ) : (
          <p className="text-sm text-betenbough-olive font-semibold mb-2">
            {card.plan_name} Floor Plan
          </p>
        )}

        <p className="text-sm text-gray-600 mb-3">
          {card.community_name} Community
        </p>

        {/* Monthly Estimate */}
        {card.monthly_estimate && (
          <p className="text-sm text-gray-700 mb-3">
            Est. ${card.monthly_estimate.toLocaleString()}/mo
          </p>
        )}

        {/* Specs */}
        <div className="flex items-center flex-nowrap gap-2 text-xs text-gray-700 mb-4">
          <div className="flex items-center gap-0.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>{card.bedrooms} bed</span>
          </div>
          <div className="flex items-center gap-0.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            <span>{card.bathrooms} bath</span>
          </div>
          <div className="flex items-center gap-0.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span>{card.sqft.toLocaleString()} sqft</span>
          </div>
          {card.garage_spaces !== undefined && card.garage_spaces !== null && card.garage_spaces > 0 && (
            <div className="flex items-center gap-0.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span>{card.garage_spaces} car</span>
            </div>
          )}
        </div>

        {/* Incentives Text */}
        {card.incentives_text && (
          <div className="mb-4 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
            {card.incentives_text}
          </div>
        )}

        {/* Built-in CTAs */}
        <div className="flex gap-2">
          <button
            onClick={() => handleCTAClick('', card.ctas.view_more.url)}
            className="flex-1 inline-flex items-center justify-center gap-1 bg-white hover:bg-gray-50 text-betenbough-olive border
                       border-betenbough-olive px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Home Details
          </button>
          {isSubmitted ? (
            <button
              disabled
              className="flex-1 inline-flex items-center justify-center gap-1 bg-green-600 text-white
                         px-2 py-1.5 rounded-lg text-xs font-medium cursor-default whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Request Submitted
            </button>
          ) : (
            <button
              onClick={() => handleFormToggle(true)}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-betenbough-olive hover:bg-betenbough-olive/90 text-white
                         px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Request Tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeCard;
